import { Stack, Text, Title, Button, ThemeIcon, Box, rem } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconInbox,
  IconSearch,
  IconFolderOff,
  IconPlus,
  IconAlertCircle,
  IconTrophy,
  IconUsers,
  IconCalendarEvent,
  IconDeviceGamepad2,
  IconShoppingCart,
  IconMail,
  IconChartBar,
} from "@tabler/icons-react";
import { ReactNode } from "react";

const useStyles = createStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing.xl,
    minHeight: rem(300),
    textAlign: "center",
  },
  iconWrapper: {
    marginBottom: theme.spacing.xs,
  },
  title: {
    color: theme.colors.dark[0],
    fontWeight: 600,
    marginTop: theme.spacing.lg,
  },
  description: {
    color: theme.colors.dark[2],
    maxWidth: rem(400),
    lineHeight: 1.6,
  },
  button: {
    marginTop: theme.spacing.lg,
  },
}));

// Icon mapping for different contexts
const contextIcons: Record<
  string,
  React.FC<{ size?: number; stroke?: number }>
> = {
  default: IconInbox,
  search: IconSearch,
  folder: IconFolderOff,
  error: IconAlertCircle,
  tournament: IconTrophy,
  team: IconUsers,
  player: IconUsers,
  event: IconCalendarEvent,
  game: IconDeviceGamepad2,
  shop: IconShoppingCart,
  email: IconMail,
  stats: IconChartBar,
};

export interface EmptyStateProps {
  title?: string;
  description?: string;
  icon?: ReactNode;
  iconType?: keyof typeof contextIcons;
  iconColor?: string;
  actionLabel?: string;
  onAction?: () => void;
  actionIcon?: ReactNode;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  children?: ReactNode;
  compact?: boolean;
}

export const EmptyState = ({
  title = "No data found",
  description = "There are no items to display at the moment.",
  icon,
  iconType = "default",
  iconColor = "cyan",
  actionLabel,
  onAction,
  actionIcon = <IconPlus size={16} />,
  secondaryActionLabel,
  onSecondaryAction,
  children,
  compact = false,
}: EmptyStateProps) => {
  const { classes } = useStyles();
  const IconComponent = contextIcons[iconType] ?? contextIcons.default;

  return (
    <Box
      className={classes.root}
      style={{ minHeight: compact ? rem(200) : rem(300) }}
    >
      {/* Icon */}
      <Box className={classes.iconWrapper}>
        <ThemeIcon
          size={compact ? 48 : 64}
          radius="xl"
          variant="light"
          color={iconColor}
        >
          {icon ?? <IconComponent size={compact ? 24 : 32} stroke={1.5} />}
        </ThemeIcon>
      </Box>

      {/* Title */}
      <Title order={compact ? 5 : 4} className={classes.title}>
        {title}
      </Title>

      {/* Description */}
      <Text
        size={compact ? "sm" : "md"}
        className={classes.description}
        mt="xs"
      >
        {description}
      </Text>

      {/* Custom content */}
      {children}

      {/* Actions */}
      {(actionLabel || secondaryActionLabel) && (
        <Stack gap="xs" mt="lg">
          {actionLabel && onAction && (
            <Button
              className={classes.button}
              leftSection={actionIcon}
              onClick={onAction}
              size={compact ? "sm" : "md"}
            >
              {actionLabel}
            </Button>
          )}
          {secondaryActionLabel && onSecondaryAction && (
            <Button
              variant="subtle"
              onClick={onSecondaryAction}
              size={compact ? "xs" : "sm"}
            >
              {secondaryActionLabel}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
};

// Pre-configured variants for common use cases
export const NoResultsState = (props: Partial<EmptyStateProps>) => (
  <EmptyState
    title="No results found"
    description="Try adjusting your search or filter to find what you're looking for."
    iconType="search"
    {...props}
  />
);

export const NoTournamentsState = (props: Partial<EmptyStateProps>) => (
  <EmptyState
    title="No tournaments yet"
    description="Create your first tournament to start managing competitive events."
    iconType="tournament"
    actionLabel="Create Tournament"
    {...props}
  />
);

export const NoTeamsState = (props: Partial<EmptyStateProps>) => (
  <EmptyState
    title="No teams found"
    description="Teams will appear here once players start forming squads."
    iconType="team"
    {...props}
  />
);

export const NoEventsState = (props: Partial<EmptyStateProps>) => (
  <EmptyState
    title="No events scheduled"
    description="Create an event to start engaging with your community."
    iconType="event"
    actionLabel="Create Event"
    {...props}
  />
);

export const NoPlayersState = (props: Partial<EmptyStateProps>) => (
  <EmptyState
    title="No players yet"
    description="Players will appear here once they register on your platform."
    iconType="player"
    {...props}
  />
);

export const ErrorState = (props: Partial<EmptyStateProps>) => (
  <EmptyState
    title="Something went wrong"
    description="We encountered an error loading this content. Please try again."
    iconType="error"
    iconColor="rose"
    actionLabel="Try Again"
    {...props}
  />
);
